import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import debounce from 'lodash.debounce';

const initialDimensions = {
  width: 0,
  height: 0,
  x: 0,
  y: 0,
  bottom: 0,
  left: 0,
  right: 0,
  top: 0,
};
const getDimensionObject = (node: HTMLElement) => {
  // NOTE: can't use spread here to destruct constraints becasue boundingRects doesnt return a real Object
  const { width, height, x, y, bottom, left, right, top } =
    node.getBoundingClientRect();
  return { width, height, x, y, bottom, left, right, top };
};

export const useIsMounted = () => {
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  return isMounted;
};

export const useBoundingRectOnce = (): [
  (node: HTMLElement | null) => void,
  DOMRect | null,
] => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const ref = useCallback((node) => {
    if (node !== null && node instanceof Element) {
      setRect(node.getBoundingClientRect());
    }
  }, []);
  return [ref, rect];
};

// Should not need return types, but for whatever reason my VSCode at least refused to work without them, even with restart TS server
export const useBoundingRect = <T extends HTMLElement>(
  limit = 100,
): [(n: T) => void, typeof initialDimensions, T | null] => {
  const isMounted = useIsMounted();
  const [dimensions, setDimensions] = useState(initialDimensions);
  const [node, setNode] = useState<T | null>(null);

  const ref = useCallback((n: T) => setNode(n), []);

  useLayoutEffect(() => {
    let frameId = 0;
    let listener: ReturnType<typeof debounce>;
    if (typeof window !== 'undefined' && node) {
      const measure = () =>
        window.requestAnimationFrame(() => {
          if (isMounted.current) {
            setDimensions(getDimensionObject(node));
          }
        });

      frameId = measure();
      listener = debounce(measure, limit);

      window.addEventListener('resize', listener);
      window.addEventListener('scroll', listener);
    }

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      if (listener) {
        window.removeEventListener('resize', listener);
        window.removeEventListener('scroll', listener);
      }
    };
  }, [node, limit, isMounted]);

  return [ref, dimensions, node];
};
